/**
 * 事务明细表格列配置
 * 根据事务类型和子类型显示不同的字段
 */

import { Tag, InputNumber, Button, Input, Select } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { PERMIT_TYPE_CONFIG, PERMIT_TYPES, getTransactionStatusOptions } from "../../Monitor/constants/transactionConstants";
import UnifiedStatusEditor from "../components/UnifiedStatusEditor";

/**
 * 许可类型的列定义
 */
export const getPermitColumns = (transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail) => {
    const statusOptions = getTransactionStatusOptions(transactionType);

    return [
        {
            title: "产品信息",
            key: "product_info",
            width: 120,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: "bold", fontSize: 12 }}>{record.productName}</div>
                    <div style={{ fontSize: 11, color: "#666" }}>{record.supplierName}</div>
                </div>
            ),
        },
        {
            title: "许可类型",
            dataIndex: "permitType", // API返回的是驼峰命名
            width: 100,
            render: (type, record) => {
                // 处理许可类型映射和显示，兼容不同的字段名
                const permitType = type || record.permit_type || record.permitType;
                const config = PERMIT_TYPE_CONFIG[permitType] || { color: "default", text: permitType || "未知" };
                return <Tag color={config.color}>{config.text}</Tag>;
            },
        },
        {
            title: "详细信息",
            key: "detail_info",
            width: 160,
            render: (_, record) => {
                // 兼容不同的字段名
            const permitType = record.permitType || record.permit_type;

            if (permitType === PERMIT_TYPES.OVERFLIGHT) {
                    // 飞越许可：显示飞越国家/地区信息
                    let countriesCount = 0;
                    let approvedCount = 0;

                    // 优先使用JSON格式的飞越国家/地区信息
                    if (record.overflightCountries && Array.isArray(record.overflightCountries)) {
                        const countries = record.overflightCountries;
                        countriesCount = countries.length;
                        approvedCount = countries.filter((c) => c.status === "APPROVED").length;
                    } else {
                        // 兼容旧格式数据
                        countriesCount = record.overflightCountriesCount || record.overflight_countries_count || 0;
                        approvedCount = record.overflightApprovedCount || record.overflight_approved_count || 0;
                    }

                    return (
                        <div>
                            <div style={{ fontSize: 12 }}>飞越国家/地区数量: {countriesCount}</div>
                            <div style={{ fontSize: 12 }}>已批复: {approvedCount}</div>
                            {countriesCount > 0 && (
                                <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                                    进度: {approvedCount}/{countriesCount}
                                </div>
                            )}
                        </div>
                    );
                } else if (permitType === PERMIT_TYPES.LANDING_TAKEOFF || permitType === PERMIT_TYPES.DEPARTURE || permitType === PERMIT_TYPES.ARRIVAL) {
                    // 起降许可：显示机场和时刻信息
                    return (
                        <div>
                            <div style={{ fontSize: 12 }}>机场: {record.airport_code || record.airportCode || "N/A"}</div>
                            <div style={{ fontSize: 12 }}>时刻: {record.airport_slot_start || record.airportSlotStart ? "已设置" : "待设置"}</div>
                            {(record.airport_slot_start || record.airportSlotStart) && <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{new Date(record.airport_slot_start || record.airportSlotStart).toLocaleString()}</div>}
                        </div>
                    );
                } else {
                    // 未知类型
                    return (
                        <div>
                            <div style={{ fontSize: 12, color: "#999" }}>类型未知</div>
                        </div>
                    );
                }
            },
        },
        {
            title: "状态",
            key: "status",
            width: 140,
            align: "center",
            render: (_, record) => <UnifiedStatusEditor record={record} transactionType={transactionType} transactionId={transactionId} isDetail={true} statusOptions={statusOptions} />,
        },
        {
            title: "备注",
            key: "remarks",
            width: 200,
            render: (_, record) => <Input.TextArea value={record.remarks || ""} onChange={(e) => handleRemarksChange(record.id, e.target.value)} disabled={isPreview} placeholder="输入备注..." autoSize={{ minRows: 1, maxRows: 3 }} size="small" />,
        },
        {
            key: "actions",
            width: 60,
            render: (_, record) => <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={isPreview} onClick={() => handleDeleteDetail(record)} />,
        },
    ];
};

/**
 * 燃油类型的列定义
 * 注意：燃油明细不包含状态字段，状态由主表管理
 */
export const getFuelColumns = (transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail) => {
    // transactionType 和 transactionId 参数保留以保持接口一致性
    return [
        {
            title: "产品信息",
            key: "product_info",
            width: 120,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: "bold", fontSize: 12 }}>{record.productName}</div>
                    <div style={{ fontSize: 11, color: "#666" }}>{record.supplierName}</div>
                </div>
            ),
        },
        {
            title: "数量",
            key: "quantity",
            width: 100,
            render: (_, record) => <InputNumber size="small" value={record.quantity} min={0} step={0.1} style={{ width: 80 }} disabled={isPreview} onChange={(value) => handleQuantityChange(record.id, value)} />,
        },
        {
            title: "燃油信息",
            key: "fuel_info",
            width: 150,
            render: (_, record) => (
                <div>
                    <div style={{ fontSize: 12 }}>类型: {record.fuel_type || "N/A"}</div>
                    <div style={{ fontSize: 12 }}>密度: {record.density || "N/A"} kg/L</div>
                    <div style={{ fontSize: 12 }}>单位: {record.unit || "N/A"}</div>
                </div>
            ),
        },
        {
            title: "备注",
            key: "remarks",
            width: 200,
            render: (_, record) => <Input.TextArea value={record.remarks || ""} onChange={(e) => handleRemarksChange(record.id, e.target.value)} disabled={isPreview} placeholder="输入备注..." autoSize={{ minRows: 1, maxRows: 3 }} size="small" />,
        },
        {
            key: "actions",
            width: 60,
            render: (_, record) => <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={isPreview} onClick={() => handleDeleteDetail(record)} />,
        },
    ];
};

/**
 * 餐食类型的列定义
 * 注意：餐食明细不包含状态字段，状态由主表管理
 * 餐食事务明细只需要备注字段
 */
export const getCateringColumns = (transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail) => {
    // transactionType 和 transactionId 参数保留以保持接口一致性
    return [
        {
            title: "产品信息",
            key: "product_info",
            width: 120,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: "bold", fontSize: 12 }}>{record.productName}</div>
                    <div style={{ fontSize: 11, color: "#666" }}>{record.supplierName}</div>
                </div>
            ),
        },
        {
            title: "数量",
            key: "quantity",
            width: 100,
            render: (_, record) => <InputNumber size="small" value={record.quantity} min={0} step={1} style={{ width: 80 }} disabled={isPreview} onChange={(value) => handleQuantityChange(record.id, value)} />,
        },
        {
            title: "备注",
            key: "remarks",
            width: 200,
            render: (_, record) => <Input.TextArea value={record.remarks || ""} onChange={(e) => handleRemarksChange(record.id, e.target.value)} disabled={isPreview} placeholder="输入备注..." autoSize={{ minRows: 1, maxRows: 3 }} size="small" />,
        },
        {
            key: "actions",
            width: 60,
            render: (_, record) => <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={isPreview} onClick={() => handleDeleteDetail(record)} />,
        },
    ];
};

/**
 * 酒店类型的列定义
 * 酒店事务明细需要人数和备注
 */
export const getHotacColumns = (transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail, handleFieldChange) => {
    return [
        {
            title: "产品信息",
            key: "product_info",
            width: 120,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: "bold", fontSize: 12 }}>{record.productName}</div>
                    <div style={{ fontSize: 11, color: "#666" }}>{record.supplierName}</div>
                </div>
            ),
        },
        {
            title: "数量",
            key: "quantity",
            width: 100,
            render: (_, record) => <InputNumber size="small" value={record.quantity} min={0} step={1} style={{ width: 80 }} disabled={isPreview} onChange={(value) => handleQuantityChange(record.id, value)} />,
        },
        {
            title: "人数",
            key: "guest_count",
            width: 100,
            render: (_, record) => <InputNumber size="small" value={record.guest_count || 1} min={1} step={1} style={{ width: 80 }} disabled={isPreview} onChange={(value) => handleFieldChange && handleFieldChange(record.id, 'guest_count', value)} placeholder="人数" />,
        },
        {
            title: "备注",
            key: "remarks",
            width: 200,
            render: (_, record) => <Input.TextArea value={record.remarks || ""} onChange={(e) => handleRemarksChange(record.id, e.target.value)} disabled={isPreview} placeholder="输入备注..." autoSize={{ minRows: 1, maxRows: 3 }} size="small" />,
        },
        {
            key: "actions",
            width: 60,
            render: (_, record) => <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={isPreview} onClick={() => handleDeleteDetail(record)} />,
        },
    ];
};

/**
 * 机组接送类型的列定义
 * 接送事务明细需要人数、线路、接送时间、备注
 */
export const getCrewTransportationColumns = (transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail, handleFieldChange) => {
    return [
        {
            title: "产品信息",
            key: "product_info",
            width: 120,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: "bold", fontSize: 12 }}>{record.productName}</div>
                    <div style={{ fontSize: 11, color: "#666" }}>{record.supplierName}</div>
                </div>
            ),
        },
        {
            title: "数量",
            key: "quantity",
            width: 100,
            render: (_, record) => <InputNumber size="small" value={record.quantity} min={0} step={1} style={{ width: 80 }} disabled={isPreview} onChange={(value) => handleQuantityChange(record.id, value)} />,
        },
        {
            title: "人数",
            key: "passenger_count",
            width: 100,
            render: (_, record) => <InputNumber size="small" value={record.passenger_count || 1} min={1} step={1} style={{ width: 80 }} disabled={isPreview} onChange={(value) => handleFieldChange && handleFieldChange(record.id, 'passenger_count', value)} placeholder="人数" />,
        },
        {
            title: "线路",
            key: "route_type",
            width: 150,
            render: (_, record) => (
                <Select
                    size="small"
                    value={record.route_type || "HOTEL_TO_AIRPORT"}
                    style={{ width: 140 }}
                    disabled={isPreview}
                    onChange={(value) => handleFieldChange && handleFieldChange(record.id, 'route_type', value)}
                    options={[
                        { value: "HOTEL_TO_AIRPORT", label: "酒店到机场" },
                        { value: "AIRPORT_TO_HOTEL", label: "机场到酒店" },
                    ]}
                />
            ),
        },
        {
            title: "接送时间",
            key: "pickup_time",
            width: 180,
            render: (_, record) => (
                <Input
                    size="small"
                    type="datetime-local"
                    value={record.pickup_time ? new Date(record.pickup_time).toISOString().slice(0, 16) : ""}
                    disabled={isPreview}
                    onChange={(e) => handleFieldChange && handleFieldChange(record.id, 'pickup_time', e.target.value ? new Date(e.target.value).toISOString() : null)}
                    style={{ width: 170 }}
                />
            ),
        },
        {
            title: "备注",
            key: "remarks",
            width: 200,
            render: (_, record) => <Input.TextArea value={record.remarks || ""} onChange={(e) => handleRemarksChange(record.id, e.target.value)} disabled={isPreview} placeholder="输入备注..." autoSize={{ minRows: 1, maxRows: 3 }} size="small" />,
        },
        {
            key: "actions",
            width: 60,
            render: (_, record) => <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={isPreview} onClick={() => handleDeleteDetail(record)} />,
        },
    ];
};

/**
 * 飞行简报类型的列定义
 * 简报事务明细需要最大载荷和备注
 */
export const getFlightBriefColumns = (transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail, handleFieldChange) => {
    return [
        {
            title: "产品信息",
            key: "product_info",
            width: 120,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: "bold", fontSize: 12 }}>{record.productName}</div>
                    <div style={{ fontSize: 11, color: "#666" }}>{record.supplierName}</div>
                </div>
            ),
        },
        {
            title: "数量",
            key: "quantity",
            width: 100,
            render: (_, record) => <InputNumber size="small" value={record.quantity} min={0} step={1} style={{ width: 80 }} disabled={isPreview} onChange={(value) => handleQuantityChange(record.id, value)} />,
        },
        {
            title: "最大载荷(kg)",
            key: "max_payload_kg",
            width: 120,
            render: (_, record) => <InputNumber size="small" value={record.max_payload_kg} min={0} step={0.1} style={{ width: 110 }} disabled={isPreview} onChange={(value) => handleFieldChange && handleFieldChange(record.id, 'max_payload_kg', value)} placeholder="载荷" />,
        },
        {
            title: "备注",
            key: "remarks",
            width: 200,
            render: (_, record) => <Input.TextArea value={record.remarks || ""} onChange={(e) => handleRemarksChange(record.id, e.target.value)} disabled={isPreview} placeholder="输入备注..." autoSize={{ minRows: 1, maxRows: 3 }} size="small" />,
        },
        {
            key: "actions",
            width: 60,
            render: (_, record) => <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={isPreview} onClick={() => handleDeleteDetail(record)} />,
        },
    ];
};

/**
 * 根据事务类型获取对应的列配置
 */
export const getColumnsByTransactionType = (transactionType, transactionId, isPreview, handlers) => {
    const { handleQuantityChange, handleRemarksChange, handleDeleteDetail, handleFieldChange } = handlers;

    switch (transactionType) {
        case "PERMIT_REQUEST":
            return getPermitColumns(transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail);
        case "FUEL_REQUEST":
            return getFuelColumns(transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail);
        case "CATERING_ORDER":
            return getCateringColumns(transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail);
        case "HOTAC":
            return getHotacColumns(transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail, handleFieldChange);
        case "CREW_TRANSPORTATION":
            return getCrewTransportationColumns(transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail, handleFieldChange);
        case "FLIGHT_BRIEF":
            return getFlightBriefColumns(transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail, handleFieldChange);
        default:
            // 默认使用通用列配置
            return getFuelColumns(transactionType, transactionId, isPreview, handleQuantityChange, handleRemarksChange, handleDeleteDetail);
    }
};
